import type { QueuedMutation, SessionDetail, SetEntry } from "@training/shared";

/**
 * A set saved without network sits in the queue, and the server's copy of the
 * session knows nothing of it. Read as is, the screen offered that set again:
 * on 11/10 a circuit went back to its second round, and the tap that followed
 * logged it twice. The queue is laid over what the server said, so a set
 * counts from the moment it was validated, sent or not.
 */
export function withPending<M extends QueuedMutation & { queuedAt?: string }>(
  detail: SessionDetail,
  queued: readonly M[],
): SessionDetail {
  if (queued.length === 0) return detail;
  return {
    ...detail,
    exercises: inPendingOrder(detail, queued).map((exercise) => {
      let sets: SetEntry[] = exercise.sets;
      for (const item of queued) {
        if (item.kind === "set.delete") {
          if (sets.some((set) => set.clientId === item.payload.clientId)) {
            sets = sets.filter((set) => set.clientId !== item.payload.clientId);
          }
        } else if (item.kind === "set.upsert" && item.payload.sessionExerciseId === exercise.id) {
          const { completedAt, ...payload } = item.payload;
          const known = sets.find((set) => set.clientId === payload.clientId);
          const pending: SetEntry = {
            ...payload,
            // Not on the server yet, so no id of its own; nothing reads it.
            id: known?.id ?? Number.MAX_SAFE_INTEGER,
            completedAt: completedAt ?? item.queuedAt ?? new Date().toISOString(),
            restBeforeSeconds: payload.restBeforeSeconds ?? null,
          };
          sets = [...sets.filter((set) => set.clientId !== payload.clientId), pending];
        }
      }
      if (sets === exercise.sets) return exercise;
      return { ...exercise, sets: [...sets].sort((a, b) => a.setIndex - b.setIndex) };
    }),
  };
}

/**
 * The order last chosen while offline, over the server's. A line it does not
 * name, added since, keeps its place after the others.
 */
function inPendingOrder(detail: SessionDetail, queued: readonly QueuedMutation[]): SessionDetail["exercises"] {
  // One a session: the queue keeps only the latest.
  const order = queued.find((item) => item.kind === "session.order" && item.payload.sessionId === detail.session.id);
  if (order?.kind !== "session.order") return detail.exercises;
  const rank = new Map(order.payload.sessionExerciseIds.map((id, index) => [id, index]));
  const last = rank.size;
  return [...detail.exercises].sort((a, b) => (rank.get(a.id) ?? last) - (rank.get(b.id) ?? last));
}

/**
 * What the pills say of the queue: nothing once it is empty, offline or not
 * — "0 en attente" read as something gone missing.
 */
export function pendingLabel(pending: number, syncing: boolean): string | null {
  if (pending === 0) return null;
  return syncing ? "Envoi…" : `${pending} en attente`;
}
